import { cleanDecode } from "../../utils/CommonUtils";
import { noItx } from "../../translationItx/itx";
import { createCatalogProduct } from "./CatalogProductSelection";
import type { CatalogProductSelection } from "./CatalogProductSelection";
import type KnxProductInfo from "../../models/KnxProductInfo";
import { decodeKnxProductInfos } from "../../models/decodeKnxProductInfo";

export type CatalogProductOption = {
  productInfo: KnxProductInfo;
  product: CatalogProductSelection;
  productType: string;
  description: string;
  variants: Array<{ product: CatalogProductSelection; description: string; variantEan: string }>;
};

export function productDescription(description: string, orderNumber: string): string {
  const text = cleanDecode(description);
  return (orderNumber ? text.split(orderNumber).join("") : text)
    .replace(/[^\S\r\n]+/g, " ").trim();
}

/** Keep one application per manufacturer/order number, preserving catalog order and variants. */
export function latestCatalogProductOptions(options: CatalogProductOption[]): CatalogProductOption[] {
  const latest = new Map<string, CatalogProductOption>();
  const keyOf = (option: CatalogProductOption) => JSON.stringify([
    option.product.manufacturer.trim(), option.product.orderNumber.trim(),
  ]);
  const versionOf = (option: CatalogProductOption) => {
    const value = cleanDecode(String(option.productInfo.ApplicationVersion ?? "")).trim();
    return value && Number.isFinite(Number(value)) ? Number(value) : -Infinity;
  };
  for (const option of options) {
    if (!option.product.orderNumber.trim()) continue;
    const key = keyOf(option);
    const previous = latest.get(key);
    if (!previous || versionOf(option) > versionOf(previous)) latest.set(key, option);
  }
  return options.filter(option => !option.product.orderNumber.trim() || latest.get(keyOf(option)) === option);
}

/** Preserves catalog families and creates selectable variants without mutating the response. */
export function catalogProductOptions(metadata: KnxProductInfo[], language?: string): CatalogProductOption[] {
  return metadata.flatMap(raw => {
    if (String(raw.Release) !== "true") return [];
    try {
      const item: KnxProductInfo & { Description?: string } = { ...raw };
      for (const key of ["ManufacturerName", "ProductType", "Description", "OrderNumber"]) {
        item[key] = cleanDecode(String(item[key] ?? ""));
      }
      const variants = Array.isArray(raw.Variants) ? raw.Variants : JSON.parse(cleanDecode(raw.Variants || "[]"));
      if (!Array.isArray(variants)) throw new Error(noItx("Product variants must be an array."));
      const [productInfo] = decodeKnxProductInfos([item], language);
      return [{
        productInfo,
        product: createCatalogProduct(productInfo),
        productType: item.ProductType || "",
        description: productDescription(productInfo.ProductName || item.Description || "", item.OrderNumber),
        variants: variants.map(variant => {
          const articleNo = cleanDecode(String(variant.articleNo ?? "")).trim();
          if (!articleNo) throw new Error(noItx("Product variant has no article number."));
          const variantEan = cleanDecode(String(variant.variantEan || variant.EAN || variant.Ean || "")).trim();
          const description = productDescription(
            productDescription(String(variant.description ?? variant.Description ?? ""), articleNo),
            item.OrderNumber,
          );
          return {
            variantEan,
            product: createCatalogProduct({ ...productInfo, VariantArticleNumber: articleNo }),
            description: productDescription(description, variantEan).split(/\||\r?\n/)
              .map(line => line.trim()).filter(line => line && !/^EAN\s*:?$/i.test(line)).join("\n"),
          };
        }),
      }];
    } catch (error) {
      console.error(noItx("Could not read product variants:"), raw.CatalogId, error);
      return [];
    }
  });
}
